import { ActiveCells, DetectionMode, DynamicAnalysis } from '@EcamModel/model/PTZEventMotionDetection';
import { Divider, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import color from '@theme/Colors';
import { ReactNode, useEffect } from 'react';
import { transformScheduleData } from 'src/helpers';
import {
    DataSectionHasSubItem,
    SectionHasSubItemHeader,
    SectionHasSubItemProps,
    SectionNotSubItem,
} from './BasicInfoMilesightCameraPTZ';
import useMileSightPTZ, { SectionsCollapsePTZ } from '@pages/cameras/hook/useMilesightPTZ';

const dynamicAnalysisEnableMapping: { [key in DynamicAnalysis]: string } = {
    [DynamicAnalysis.Disable]: 'Disable',
    [DynamicAnalysis.Enable]: 'Enable',
};

const activeCellSettingMapping: { [key in ActiveCells]: string } = {
    [ActiveCells.Normal]: 'Normal',
    [ActiveCells.Compatible]: 'Compatible',
};

const detectionModeMapping: { [key in DetectionMode]: string } = {
    [DetectionMode.Normal]: 'Normal',
    [DetectionMode.Advanced]: 'Advanced',
};

type Props = {
    isOpenCollapse: { [key in SectionsCollapsePTZ]: boolean };
    handleToggleCollapse: (section: SectionsCollapsePTZ) => void;
};

export default function MotionDetectionMilesightsCameraPTZ(props: Props) {
    const handleEdit = () => {
        console.log('Edit button clicked');
    };

    const { camera } = useCameraDetailContext();
    const { isOpenCollapse, handleToggleCollapse } = props;
    const PTZMotionDetection = camera?.PTZCamera?.PTZCameraConfig?.EventMotionDetection;

    const statusList_MotionDetectionHeader: DataSectionHasSubItem[] = [
        {
            label: 'Enable detection:',
            value: <Typography>{PTZMotionDetection?.DynamicAnalysisEnable! ? 'Yes' : 'No'}</Typography>,
        },
        {
            label: 'Enable motion analysis:',
            value: <Typography>{PTZMotionDetection?.MotionEnable! ? 'Yes' : 'No'}</Typography>,
        },
    ];

    const statusList_MotionDetection_BasicSettings: DataSectionHasSubItem[] = [
        { label: 'Mode:', value: <Typography>{detectionModeMapping[PTZMotionDetection?.DetectionMode!]}</Typography> },
        { label: 'Sensitivity:', value: <Typography>{PTZMotionDetection?.TriggerSensitivity!}</Typography> },
        {
            label: 'Onvif Motion ActiveCells Settings:',
            value: <Typography>{activeCellSettingMapping[PTZMotionDetection?.Activecell!]}</Typography>,
        },
    ];

    const dataSectionHasSubItems: ReactNode | DataSectionHasSubItem[] = transformScheduleData(
        PTZMotionDetection?.ScheduleList!,
        (dayName, schedule) => ({
            label: `${dayName}:`,
            value: schedule,
        })
    );

    const statusList_MotionDetection_ScheduleSettings: SectionHasSubItemProps = {
        title: '',
        data: dataSectionHasSubItems,
    };

    return (
        <Stack id="motionDetection_BasicSettings" border={'1px solid #E4E7EC'} borderRadius={2} flex={1}>
            {/* Basic Information */}
            <SectionHasSubItemHeader id="" title="Motion detection" onEdit={handleEdit} />

            <Divider
                orientation="vertical"
                sx={{
                    borderBottomWidth: 1,
                    borderColor: '#E4E7EC',
                    mx: 2,
                }}
            />

            <Stack width={'100%'} padding={'16px 16px'} bgcolor={color.white} gap={2}>
                {statusList_MotionDetectionHeader.map((item, index) => {
                    return (
                        <Grid key={index} xs={12} container>
                            <Grid xs={5}>
                                <Typography>{item.label}</Typography>
                            </Grid>
                            <Grid xs={7}>{item.value}</Grid>
                        </Grid>
                    );
                })}
            </Stack>

            <SectionNotSubItem
                header="Basic Settings"
                statusList={statusList_MotionDetection_BasicSettings}
                isOpen={isOpenCollapse.motionDetection_BasicSettings}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.MotionDetection_BasicSettings);
                }}
            />

            <SectionNotSubItem
                name="motionDetection_ScheduleSettings"
                header="Schedule Settings"
                statusList={statusList_MotionDetection_ScheduleSettings.data}
                isOpen={isOpenCollapse.motionDetection_ScheduleSettings}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.MotionDetection_ScheduleSettings);
                }}
            />
        </Stack>
    );
}
